import { inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { ApiAuthService, AuthService, VendorContextService } from '@shared';
import { firstValueFrom } from 'rxjs';

type VendorState = 'unauthenticated' | 'pending-onboarding' | 'onboarded';

async function evaluateVendorState(): Promise<VendorState> {
  console.log('[VendorAuthGuard] Evaluating vendor state...');
  const authService = inject(AuthService);
  const apiAuthService = inject(ApiAuthService);
  const vendorContext = inject(VendorContextService);

  // Check if JWT token exists and is valid (not expired)
  if (!apiAuthService.isAuthenticated()) {
    // If token exists but is expired, try to refresh
    const refreshToken = apiAuthService.getRefreshToken();
    if (refreshToken) {
      try {
        console.log('[VendorAuthGuard] Token expired, attempting refresh...');
        await firstValueFrom(apiAuthService.refreshToken());
        console.log('[VendorAuthGuard] Token refreshed successfully');
        // Token refreshed, continue with auth check
      } catch (error) {
        console.log('[VendorAuthGuard] Token refresh failed, state: unauthenticated');
        // Refresh failed, clear tokens and return unauthenticated
        apiAuthService.clearTokens();
        return 'unauthenticated';
      }
    } else {
      console.log('[VendorAuthGuard] No JWT token found, state: unauthenticated');
      return 'unauthenticated';
    }
  }

  // Get profile from auth service
  let profile = authService.profile();
  
  // If profile not loaded, try to load from JWT user data
  if (!profile) {
    const jwtUser = apiAuthService.getUser();
    if (jwtUser) {
      const userProfile = apiAuthService.convertToUserProfile(jwtUser);
      authService.profile.set(userProfile);
      profile = userProfile;
    }
  }

  // Verify user has vendor role
  if (!profile || profile.role !== 'vendor') {
    console.log('[VendorAuthGuard] User does not have vendor role, state: unauthenticated');
    return 'unauthenticated';
  }

  try {
    console.log('[VendorAuthGuard] Ensuring vendor ID...');
    await vendorContext.ensureVendorId();
    console.log('[VendorAuthGuard] Waiting for vendor snapshot...');
    const vendor = await vendorContext.waitForVendorSnapshot();
    console.log('[VendorAuthGuard] Vendor snapshot received:', vendor ? 'exists' : 'null');
    
    // If vendor doesn't exist but user has vendor role, they need to complete onboarding
    // This is expected for new vendors who just logged in
    if (!vendor) {
      console.log('[VendorAuthGuard] No vendor profile found, but user has vendor role - allowing onboarding');
      return 'pending-onboarding';
    }
    
    // Vendor exists, check onboarding status
    // Check onboarding status directly on the vendor object, not on the signal
    // (signal might not have updated yet even though waitForVendorSnapshot returned a vendor)
    const onboardingStatus = vendor.onboardingStatus;
    const onboardingCompletedAt = vendor.onboardingCompletedAt;
    
    // Check if onboarded (same logic as VendorContextService.isOnboarded)
    // Handle onboardingCompletedAt as Date, number (timestamp), or string (ISO date)
    const hasCompletedAt = onboardingCompletedAt != null && onboardingCompletedAt !== undefined;
    const isOnboarded = 
      onboardingStatus === 'submitted' ||
      onboardingStatus === 'completed' ||
      hasCompletedAt;
    
    console.log('[VendorAuthGuard] Vendor onboarding check:', {
      onboardingStatus: onboardingStatus || 'NOT SET',
      onboardingCompletedAt: hasCompletedAt ? 'SET' : 'NOT SET',
      isOnboarded
    });
    
    const state = isOnboarded ? 'onboarded' : 'pending-onboarding';
    console.log('[VendorAuthGuard] State:', state);
    return state;
  } catch (error) {
    console.error('[VendorAuthGuard] Error evaluating vendor state:', error);
    console.error('[VendorAuthGuard] Error stack:', error instanceof Error ? error.stack : 'N/A');
    // If there's an error but user has vendor role, allow onboarding as fallback
    // This prevents blocking legitimate users due to transient errors
    if (profile && profile.role === 'vendor') {
      console.log('[VendorAuthGuard] Error occurred but user has vendor role - allowing onboarding as fallback');
      return 'pending-onboarding';
    }
    // If no vendor role, return unauthenticated
    return 'unauthenticated';
  }
}

export async function redirectIfAuthenticated(): Promise<boolean | UrlTree> {
  console.log('[VendorAuthGuard] redirectIfAuthenticated called');
  const router = inject(Router);
  const state = await evaluateVendorState();

  if (state === 'unauthenticated') {
    console.log('[VendorAuthGuard] redirectIfAuthenticated: allowing access (unauthenticated)');
    return true;
  }

  const redirectPath = state === 'onboarded' ? ['/tabs/orders'] : ['/onboarding/details'];
  console.log('[VendorAuthGuard] redirectIfAuthenticated: redirecting to', redirectPath);
  return router.createUrlTree(redirectPath);
}

export async function requireVendorLogin(): Promise<boolean | UrlTree> {
  const router = inject(Router);
  const state = await evaluateVendorState();

  if (state === 'unauthenticated') {
    return router.createUrlTree(['/auth/login']);
  }

  if (state === 'onboarded') {
    return router.createUrlTree(['/tabs/orders']);
  }

  return true;
}

export async function requireVendorAuthenticated(): Promise<boolean | UrlTree> {
  console.log('[VendorAuthGuard] requireVendorAuthenticated called');
  const router = inject(Router);
  const currentUrl = router.url;
  console.log('[VendorAuthGuard] Current URL:', currentUrl);
  
  try {
    const state = await evaluateVendorState();

    if (state === 'unauthenticated') {
      console.log('[VendorAuthGuard] requireVendorAuthenticated: redirecting to login');
      return router.createUrlTree(['/auth/login']);
    }

    if (state === 'pending-onboarding') {
      // Only redirect to onboarding if not already on an onboarding page
      if (!currentUrl.startsWith('/onboarding')) {
        console.log('[VendorAuthGuard] requireVendorAuthenticated: redirecting to onboarding');
        return router.createUrlTree(['/onboarding/details']);
      }
      // If already on onboarding, allow it
      console.log('[VendorAuthGuard] requireVendorAuthenticated: allowing onboarding page');
      return true;
    }

    console.log('[VendorAuthGuard] requireVendorAuthenticated: allowing access (onboarded)');
    return true;
  } catch (error) {
    console.error('[VendorAuthGuard] Error in requireVendorAuthenticated:', error);
    console.error('[VendorAuthGuard] Error stack:', error instanceof Error ? error.stack : 'N/A');
    // Allow navigation on error to prevent blocking (but log it)
    console.log('[VendorAuthGuard] Allowing navigation due to error (fallback)');
    return true;
  }
}

